/** Tipos de regla en el formulario unificado de promociones / precios por volumen. */
export const RULE_TYPES = [
    {
        id: 'promotion',
        label: 'Promoción en vitrina',
        description: 'Modifica el precio visible del producto (descuento clásico).',
    },
    {
        id: 'quantity_discount',
        label: 'Descuento por cantidad',
        description: 'Precio según volumen en el carrito; no cambia el precio en catálogo.',
    },
];

export const TIER_SCOPE_TYPES = [
    { id: 'store', label: 'Toda la tienda' },
    { id: 'collection', label: 'Categorías' },
    { id: 'product', label: 'Productos específicos' },
];

export const TIER_DISCOUNT_TYPES = [
    { id: 'percentage', label: 'Porcentaje' },
    { id: 'fixed', label: 'Monto fijo' },
];

const newLocalId = () => `tier-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;

export const createEmptyTier = (overrides = {}) => ({
    localId: newLocalId(),
    min_quantity: '1',
    discount_value: '',
    ...overrides,
});

/** Convierte umbrales API → filas UI (solo min_quantity; el rango se muestra en pantalla). */
export const thresholdsToTiers = (thresholds = []) => {
    const sorted = [...thresholds].sort((a, b) => a.min_quantity - b.min_quantity);
    if (sorted.length === 0) {
        return [createEmptyTier()];
    }
    return sorted.map((t) => ({
        localId: newLocalId(),
        min_quantity: String(t.min_quantity),
        discount_value: String(t.discount_value),
    }));
};

/** Convierte filas UI → umbrales API (solo min_quantity + discount_value). */
export const tiersToThresholds = (tiers) => {
    const parsed = tiers
        .map((t) => ({
            min_quantity: Number(t.min_quantity),
            discount_value: Number(t.discount_value),
        }))
        .filter((t) => Number.isFinite(t.min_quantity) && t.min_quantity >= 1);

    parsed.sort((a, b) => a.min_quantity - b.min_quantity);

    const seen = new Set();
    return parsed.filter((t) => {
        if (seen.has(t.min_quantity)) return false;
        seen.add(t.min_quantity);
        return true;
    });
};

export const TIER_SCOPE_LABELS = {
    store: 'Toda la tienda',
    collection: 'Categorías',
    product: 'Productos',
};

/** Payload PUT: el API no permite cambiar scope después de crear la regla. */
export const buildTierUpdatePayload = ({
    name,
    discount_type,
    thresholds,
    active,
    starts_at,
    ends_at,
    useSchedule,
}) => {
    const payload = {
        name: name.trim(),
        discount_type,
        thresholds,
        active: Boolean(active),
    };
    if (useSchedule) {
        payload.starts_at = starts_at;
        payload.ends_at = ends_at;
    }
    return payload;
};

export const formatTierDiscountValue = (discountType, discountValue, currency) => {
    if (discountType === 'percentage') {
        return `${discountValue}%`;
    }
    return currency ? `${discountValue} ${currency}` : String(discountValue);
};

/** Etiquetas por umbral para listado (chips) y tooltip. */
export const getTierThresholdDisplayItems = (tier, currency) => {
    const thresholds = Array.isArray(tier?.thresholds) ? tier.thresholds : [];
    const sorted = [...thresholds].sort((a, b) => a.min_quantity - b.min_quantity);

    return sorted.map((t) => {
        const value = formatTierDiscountValue(tier.discount_type, t.discount_value, currency);
        const min = t.min_quantity;
        return {
            key: min,
            chipLabel: `${min}+ → ${value}`,
        };
    });
};

export const formatTierRuleSummary = (tier, currency) => {
    const items = getTierThresholdDisplayItems(tier, currency);
    if (items.length === 0) return '—';
    return items.map((i) => i.chipLabel).join(' · ');
};

export const parsePricingTiersListResponse = (raw) => {
    if (Array.isArray(raw)) return raw;
    if (raw && typeof raw === 'object' && Array.isArray(raw.data)) return raw.data;
    return [];
};

const UUID_RE =
    /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

/** Evita usar IDs o UUIDs como nombre visible en la UI. */
export const resolveTierTargetDisplayName = (rawName, id) => {
    if (rawName == null || rawName === '') return null;
    const name = String(rawName).trim();
    if (!name) return null;
    if (String(id) != null && name === String(id)) return null;
    if (UUID_RE.test(name)) return null;
    if (/^\d+$/.test(name)) return null;
    if (/^Producto\s+/i.test(name) && String(id) && name.includes(String(id))) return null;
    return name;
};

export const productNeedsNameResolve = (product) =>
    !resolveTierTargetDisplayName(product?.name, product?.id);

/** Resuelve nombres de categorías/productos al editar una regla (catálogo + campos anidados del API). */
export const mapTierTargetsToSelection = (targets, collectionsCatalog = []) => {
    const selectedCollections = [];
    const selectedProducts = [];
    const list = Array.isArray(targets) ? targets : [];

    list.forEach((target) => {
        if (target.collection_id) {
            const id = target.collection_id;
            const fromCatalog = collectionsCatalog.find((c) => String(c.id) === String(id));
            const nested = target.collection || target.store_collection;
            const name =
                resolveTierTargetDisplayName(fromCatalog?.name, id) ||
                resolveTierTargetDisplayName(nested?.name, id) ||
                resolveTierTargetDisplayName(target.collection_name, id) ||
                resolveTierTargetDisplayName(target.name, id) ||
                'Categoría';
            selectedCollections.push({
                id,
                name,
                picture_url: fromCatalog?.picture_url || nested?.picture_url,
            });
        }
        if (target.product_id) {
            const id = target.product_id;
            const nested = target.product;
            const productName =
                resolveTierTargetDisplayName(nested?.name, id) ||
                resolveTierTargetDisplayName(target.product_name, id) ||
                resolveTierTargetDisplayName(target.name, id) ||
                '';
            selectedProducts.push({
                id,
                name: productName,
                sku: nested?.sku || target.sku,
                picture_url: nested?.picture_url || target.picture_url,
                images: nested?.images || target.images,
            });
        }
    });

    return { selectedCollections, selectedProducts };
};

export const mapPricingTierToListRow = (tier) => ({
    ...tier,
    ruleKind: 'quantity_discount',
    price_strategy: null,
    product_count: null,
    status: tier.active === false ? 'inactive' : 'active',
});

/** Ordena niveles por cantidad mínima sin recalcular otros campos. */
export const sortTiersByMinQuantity = (tiers) =>
    [...tiers].sort(
        (a, b) => Number(a.min_quantity) - Number(b.min_quantity)
    );

/**
 * Cada nivel (ordenado por min) debe tener cantidad mínima estrictamente mayor que el anterior.
 */
export const validateSortedTierMinQuantities = (sortedTiers) => {
    for (let i = 0; i < sortedTiers.length; i += 1) {
        const min = Number(sortedTiers[i].min_quantity);
        if (!Number.isFinite(min) || min < 1) {
            return {
                valid: false,
                message: 'Revisa las cantidades mínimas de cada nivel',
            };
        }
        if (i > 0) {
            const prev = Number(sortedTiers[i - 1].min_quantity);
            if (Number.isFinite(prev) && min <= prev) {
                return {
                    valid: false,
                    message: 'La cantidad mínima debe ser mayor al nivel anterior',
                };
            }
        }
    }
    return { valid: true };
};

/** Mensaje de error inline para el campo min_quantity de un nivel (tiers ya ordenados). */
export const getTierMinQuantityError = (sortedTiers, index) => {
    const raw = sortedTiers[index]?.min_quantity;
    if (raw === '' || raw == null) {
        return 'Ingresa una cantidad mínima';
    }

    const min = Number(raw);
    if (!Number.isFinite(min) || min < 1) {
        return 'Ingresa una cantidad mínima válida (1 o más)';
    }
    if (index > 0) {
        const prev = Number(sortedTiers[index - 1].min_quantity);
        if (Number.isFinite(prev) && min <= prev) {
            return 'La cantidad mínima debe ser mayor al nivel anterior';
        }
    }
    return null;
};

/**
 * Cada nivel (ordenado por min) debe tener descuento estrictamente mayor que el anterior.
 */
export const validateSortedTierDiscountValues = (sortedTiers, discountType = 'percentage') => {
    for (let i = 0; i < sortedTiers.length; i += 1) {
        const raw = sortedTiers[i]?.discount_value;
        const value = Number(raw);
        if (raw === '' || raw == null || !Number.isFinite(value) || value <= 0) {
            return {
                valid: false,
                message: 'Ingresa un valor de descuento válido en cada nivel',
            };
        }
        if (discountType === 'percentage' && value > 100) {
            return {
                valid: false,
                message: 'El porcentaje no puede ser mayor a 100',
            };
        }
        if (i > 0) {
            const prev = Number(sortedTiers[i - 1].discount_value);
            if (Number.isFinite(prev) && value <= prev) {
                return {
                    valid: false,
                    message: 'El descuento debe ser mayor al nivel anterior',
                };
            }
        }
    }
    return { valid: true };
};

/** Mensaje de error inline para discount_value de un nivel (tiers ya ordenados). */
export const getTierDiscountValueError = (sortedTiers, index, discountType = 'percentage') => {
    const raw = sortedTiers[index]?.discount_value;
    if (raw === '' || raw == null) {
        return 'Ingresa un valor de descuento';
    }

    const value = Number(raw);
    if (!Number.isFinite(value) || value <= 0) {
        return 'Ingresa un valor de descuento válido';
    }
    if (discountType === 'percentage' && value > 100) {
        return 'El porcentaje no puede ser mayor a 100';
    }
    if (index > 0) {
        const prev = Number(sortedTiers[index - 1].discount_value);
        if (Number.isFinite(prev) && value <= prev) {
            return 'El descuento debe ser mayor al nivel anterior';
        }
    }
    return null;
};

/** Valida todos los niveles del formulario (cantidades y descuentos). */
export const validateTierForm = (tiers, discountType = 'percentage') => {
    const sorted = sortTiersByMinQuantity(tiers);
    const minCheck = validateSortedTierMinQuantities(sorted);
    if (!minCheck.valid) return minCheck;
    return validateSortedTierDiscountValues(sorted, discountType);
};

/** Etiqueta de rango solo para la UI (el API guarda únicamente min_quantity). */
export const formatTierQuantityRange = (sortedTiers, index) => {
    const min = Number(sortedTiers[index]?.min_quantity);
    if (!Number.isFinite(min) || min < 1) return '';

    const nextMin = Number(sortedTiers[index + 1]?.min_quantity);
    if (Number.isFinite(nextMin) && nextMin > min) {
        return `${min}–${nextMin - 1} unidades`;
    }
    return `${min}+ unidades`;
};
